import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { api, formatCurrency } from '../../utils/api'
import MultiSelectDropdown from '../shared/MultiSelectDropdown'
import ProgressBar from '../shared/ProgressBar'
import { ConsignmentBadge } from '../shared'
import EditPriceModal from './EditPriceModal'
import RepriceReviewOverlay from './RepriceReviewOverlay'
import ProductDetailModal from '../shared/ProductDetailModal'
import { useAuth } from '../../hooks/useAuth'
import { useBulkMarketPriceLookup, isTerminal } from './useBulkMarketPriceLookup'
import { formatLastRepriced } from './repriceMath'
import { SUBS_BY_MAIN, getMainCategories } from '../../utils/constants'
import { DEMAND_TONE, DEMAND_TIP } from '../deals/quote-assessment/EngineReadBlock'

// ---------------------------------------------------------------------------
// Category filter. Comes from the master category list
// (shared/categories/categories.json). CATEGORY_TREE holds every sub TAGGED
// under each top level, so filtering by a top level includes its shared subs.
// Dropdown order stays A to Z, as this screen has always shown it.
// ---------------------------------------------------------------------------
const CATEGORY_TREE = SUBS_BY_MAIN
const MAIN_CATEGORIES = Object.keys(CATEGORY_TREE).sort()
// Sub to main for the table column: the sub's first top level.
const SUB_TO_MAIN = Object.fromEntries(
  Object.values(CATEGORY_TREE).flat().map((sub) => [sub, getMainCategories(sub)[0]]),
)

// Price-point bands, derived from the ANCHOR. [lo, hi) in cents.
const PRICE_BANDS = [
  { label: '£0-25',     lo: 0,      hi: 2500 },
  { label: '£25-50',    lo: 2500,   hi: 5000 },
  { label: '£50-100',   lo: 5000,   hi: 10000 },
  { label: '£100-150',  lo: 10000,  hi: 15000 },
  { label: '£150-200',  lo: 15000,  hi: 20000 },
  { label: '£200-300',  lo: 20000,  hi: 30000 },
  { label: '£300-500',  lo: 30000,  hi: 50000 },
  { label: '£500-1000', lo: 50000,  hi: 100000 },
  { label: '£1000+',    lo: 100000, hi: Infinity },
]
function anchorBandLabel(cents) {
  if (cents == null) return '—'
  const b = PRICE_BANDS.find(b => cents >= b.lo && cents < b.hi)
  return b ? b.label : '—'
}

// Discrete whole-month age options for the multi-select: one line per month,
// plus a "12 months+" bucket. Values are the month integer as a string (the
// API's age_months param; 12 = "12 months or older").
const AGE_MONTH_OPTIONS = [
  { value: '0', label: '< 1 month' },
  ...Array.from({ length: 11 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1} month${i === 0 ? '' : 's'}`,
  })),
  { value: '12', label: '12 months+' },
]

// Brand colours per audiotrader-prism/infrastructure/superset/shared/label_colors.yaml
const CHANNEL_COLORS = {
  shopify: { dot: '#96BF48', label: 'D2C' },     // Shopify green
  reverb:  { dot: '#E4002B', label: 'Reverb' },  // Reverb red
  ebay:    { dot: '#0064D2', label: 'eBay' },    // eBay blue
}
const CHANNEL_ORDER = ['shopify', 'ebay', 'reverb']
// Consignment accent (matches the badge-purple / Rules-page curve colour).
const CONSIGNMENT_COLOR = '#9333ea'
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]
// Sortable columns → API sort_by values (computed channel prices aren't sortable).
const SORT_COLUMNS = { product: 'Product', category: 'Category', age: 'Age', anchor: 'Anchor', cost: 'Cost', last_repriced: 'Last repriced' }

function formatPence(cents) {
  return cents == null ? '—' : formatCurrency(cents / 100)
}

// One-line status for the bulk market-price lookup progress bar. `skipped`
// includes items already priced this month (the whole point of the feature) and,
// when the monthly budget runs out mid-run, the un-run remainder.
function bulkStatusText(job) {
  const done = job.done || 0, skipped = job.skipped || 0, errors = job.errors || 0
  const tail = `${done} priced · ${skipped} skipped${errors ? ` · ${errors} error${errors > 1 ? 's' : ''}` : ''}`
  if (job.status === 'budget_exhausted') return `Stopped — monthly budget reached · ${tail}`
  if (job.status === 'failed') return `Failed · ${tail}`
  if (isTerminal(job.status)) return `Done · ${tail}`
  return `Looking up market prices… ${tail}`
}

// ISO date (YYYY-MM-DD) for N whole months before today — powers the "older
// than" quick buttons (older than N months ⇒ created on/before that date).
function isoMonthsAgo(n) {
  const d = new Date()
  d.setUTCMonth(d.getUTCMonth() - n)
  return d.toISOString().slice(0, 10)
}

// Resolver skip_reason → operator-readable label (resolver.py). `under_age` is
// NOT here: it gets the dedicated "launch" treatment in renderChannelCell.
const SKIP_REASON_LABELS = {
  master_off:               'engine off',
  channel_disabled:         'channel off',
  inactive:                 'inactive',
  out_of_stock:             'out of stock',
  not_listed_live:          'not listed',
  no_anchor:                'no anchor',
  // Consignment curve gates (spec_20260918): dark by default, or a legacy item
  // whose frozen terms are missing/out of range.
  consignment_disabled:     'curve off',
  consignment_terms_unknown:'terms missing',
}

// Map one API proposal item to a display row.
function toRow(item) {
  const byChannel = {}
  for (const c of item.channels || []) byChannel[c.channel] = c
  return {
    ...item,
    main_category: SUB_TO_MAIN[item.product_type] || '—',
    sub_category: item.product_type || '—',
    byChannel,
  }
}

function PricingManagement() {
  const { hasPermission } = useAuth()
  const canWrite = hasPermission('pricing:write')
  const [editProduct, setEditProduct] = useState(null)  // row open in the edit-price modal
  const [reviewItems, setReviewItems] = useState(null)  // rows open in the reprice review overlay (null = closed)
  const [detailProduct, setDetailProduct] = useState(null)  // Shopify product open in the view-only listing dialog
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  // Sums over the same filtered set as `total` (server-computed, not the page).
  const [totals, setTotals] = useState({ value: null, cost: null })
  const [offset, setOffset] = useState(0)
  const [summary, setSummary] = useState(null)
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Row selection for the bulk actions (market-price lookup, reprice review).
  const [selected, setSelected] = useState(() => new Set())
  // Bulk market-price lookup: enqueue a background job for the selected products
  // (skipping any already priced this month) and track its progress.
  const bulk = useBulkMarketPriceLookup()

  // Filters apply SERVER-SIDE on an explicit Apply (not live): searchTerm etc.
  // are the *staged* inputs; `applied` is the snapshot the query runs against,
  // so total/pagination reflect the filtered set. Sorting + page size stay live.
  const [searchTerm, setSearchTerm] = useState('')
  const [mainCat, setMainCat] = useState('all')
  const [band, setBand] = useState('all')
  const [marketPrice, setMarketPrice] = useState('all')  // 'all' | 'has' | 'none'
  const [ownership, setOwnership] = useState('all')  // 'all' | 'owned' | 'consignment'
  const [ageMonths, setAgeMonths] = useState([])  // multi-select of AGE_MONTH_OPTIONS values
  const [pageSize, setPageSize] = useState(50)
  const [sortBy, setSortBy] = useState(null)  // one of SORT_COLUMNS keys, or null
  const [sortDir, setSortDir] = useState('asc')
  // Legacy-repricing parity filters (under the "Show legacy filters" toggle).
  const [productIds, setProductIds] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)

  // The applied filter snapshot the query reads. `applyFilters` copies the
  // staged inputs here; until then, typing/selecting doesn't fire a request.
  const [applied, setApplied] = useState({
    q: '',
    mainCat: 'all',
    band: 'all',
    marketPrice: 'all',
    ownership: 'all',
    ageMonths: [],
    productIds: '',
    createdFrom: '',
    createdTo: '',
  })

  // Translate the UI filter state into the API's query params. Bands → numeric
  // ranges; category → its sub-types (the API filters on canonical product_type).
  // One place so the half-open [lo, hi) band edges match the backend exactly.
  const buildQuery = useCallback((nextOffset) => {
    const params = new URLSearchParams()
    params.set('limit', String(pageSize))
    params.set('offset', String(nextOffset))
    if (applied.q) params.set('q', applied.q)
    if (applied.mainCat !== 'all') {
      for (const sub of CATEGORY_TREE[applied.mainCat] || []) params.append('product_types', sub)
    }
    for (const m of applied.ageMonths) params.append('age_months', m)
    if (applied.band !== 'all') {
      const b = PRICE_BANDS.find(x => x.label === applied.band)
      if (b) {
        if (b.lo > 0) params.set('min_anchor_cents', String(b.lo))
        if (b.hi !== Infinity) params.set('max_anchor_cents', String(b.hi))
      }
    }
    if (applied.marketPrice === 'has') params.set('has_market_price', 'true')
    else if (applied.marketPrice === 'none') params.set('has_market_price', 'false')
    if (applied.ownership !== 'all') params.set('ownership', applied.ownership)
    if (applied.productIds) {
      for (const id of applied.productIds.split(',').map(s => s.trim()).filter(Boolean)) {
        params.append('product_ids', id)
      }
    }
    if (applied.createdFrom) params.set('created_from', applied.createdFrom)
    if (applied.createdTo) params.set('created_to', applied.createdTo)
    if (sortBy) {
      params.set('sort_by', sortBy)
      params.set('sort_dir', sortDir)
    }
    return params.toString()
  }, [applied, pageSize, sortBy, sortDir])

  const loadPage = useCallback(async (nextOffset) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get(`/api/pricing/proposals?${buildQuery(nextOffset)}`)
      setRows((resp.items || []).map(toRow))
      setTotal(resp.total || 0)
      setTotals({ value: resp.total_value_cents ?? null, cost: resp.total_cost_cents ?? null })
      setOffset(nextOffset)
      setSelected(new Set())
    } catch (e) {
      setError(e.message || 'Failed to load pricing proposals')
    } finally {
      setLoading(false)
    }
  }, [buildQuery])

  // Apply the staged inputs → triggers a reload (loadPage's identity tracks
  // `applied` via buildQuery). A new object each click, so Apply always re-runs.
  function applyFilters() {
    setApplied({
      q: searchTerm.trim(),
      mainCat,
      band,
      marketPrice,
      ownership,
      ageMonths,
      productIds: productIds.trim(),
      createdFrom,
      createdTo,
    })
  }

  function clearFilters() {
    setSearchTerm('')
    setMainCat('all')
    setBand('all')
    setMarketPrice('all')
    setOwnership('all')
    setAgeMonths([])
    setProductIds('')
    setCreatedFrom('')
    setCreatedTo('')
    setApplied({ q: '', mainCat: 'all', band: 'all', marketPrice: 'all', ownership: 'all', ageMonths: [], productIds: '', createdFrom: '', createdTo: '' })
  }

  // True when the staged inputs differ from what's applied — used to highlight Apply.
  const hasPendingChanges =
    searchTerm.trim() !== applied.q ||
    mainCat !== applied.mainCat ||
    band !== applied.band ||
    marketPrice !== applied.marketPrice ||
    ownership !== applied.ownership ||
    ageMonths.join(',') !== applied.ageMonths.join(',') ||
    productIds.trim() !== applied.productIds ||
    createdFrom !== applied.createdFrom ||
    createdTo !== applied.createdTo

  // Initial load + reload from page 0 whenever a filter changes (loadPage's
  // identity tracks the filters via buildQuery).
  useEffect(() => { loadPage(0) }, [loadPage])
  useEffect(() => {
    api.get('/api/pricing/summary').then(setSummary).catch(() => setSummary(null))
    api.get('/api/pricing/config').then(setConfig).catch(() => setConfig(null))
  }, [])

  // Aggregate KPIs come from the persisted /summary snapshot (whole universe),
  // not the loaded page. Repricing scope is the SHOPIFY channel's in_scope —
  // NOT summary.total_in_scope, which sums in_scope across all 3 channels × 2
  // cohorts and so triple-counts (~21 real items would read as ~60+). One
  // channel is the honest per-product "in scope for repricing" count. Exposure /
  // would-raise / below-cost stay summed across channels (they are per-channel
  // money/row tallies, not a product count, so summing is correct).
  const kpis = useMemo(() => {
    if (!summary) return null
    // D2C (shopify) numbers only, incl. its SALE cohort: the engine pushes D2C
    // exclusively — eBay/Reverb follow D2C via the fan-out, so their per-channel
    // stats are display-only and summing them would triple-count products
    // (operator catch 2026-07-14: "Would raise 1,950" vs 1,745 in stock).
    const d2c = [...(summary.channels || []), ...(summary.sale_channels || [])]
      .filter(c => c.channel === 'shopify')
    return {
      exposure: d2c.reduce((s, c) => s + (c.exposure_delta_cents || 0), 0),
      wouldRaise: d2c.reduce((s, c) => s + (c.would_raise || 0), 0),
      belowCost: d2c.reduce((s, c) => s + (c.below_cost || 0), 0),
    }
  }, [summary])

  const pageStart = total === 0 ? 0 : offset + 1
  const pageEnd = Math.min(offset + pageSize, total)
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const currentPage = Math.floor(offset / pageSize) + 1

  // Clicking a column header sorts by it (toggling asc/desc); the buildQuery
  // dependency change reloads from page 0.
  function toggleSort(key) {
    if (sortBy === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortBy(key); setSortDir('asc') }
  }
  const sortTh = (key, width) => (
    <th
      style={{ width, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => toggleSort(key)}
      title="Sort by this column"
    >
      {SORT_COLUMNS[key]}{sortBy === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  )

  function toggleSelect(pid) {
    setSelected(prev => {
      const n = new Set(prev)
      n.has(pid) ? n.delete(pid) : n.add(pid)
      return n
    })
  }
  function toggleSelectAll() {
    setSelected(prev => (prev.size === rows.length ? new Set() : new Set(rows.map(r => r.shopify_product_id))))
  }

  // Kick off a bulk market-price lookup for the selected rows. Errors (incl. a
  // 409 "already running", handled inside the hook by re-attaching) surface via
  // bulk.error / the progress panel — no throw to the user here.
  async function startBulkLookup() {
    const ids = [...selected]
    if (ids.length === 0) return
    try { await bulk.start(ids) } catch { /* surfaced via bulk.error */ }
  }

  // Open the reprice review queue over the selected rows (in table order). All
  // selected ids are on the current page, so they resolve from `rows`.
  function startReview() {
    const picked = rows.filter(r => selected.has(r.shopify_product_id))
    if (picked.length > 0) setReviewItems(picked)
  }

  // Open the listing (photos, description) for one row. View only: prices are
  // changed through Edit, the listing itself through Inventory.
  async function openDetail(productId) {
    setError(null)
    try {
      const resp = await api.get(`/api/inventory/shopify/product/${productId}`)
      setDetailProduct(resp.product)
    } catch (e) {
      setError(e.message || 'Failed to load the product')
    }
  }

  function renderChannelCell(p, channel) {
    const c = p.byChannel[channel]
    if (!c) return <span style={{ color: '#6b7280' }}>&mdash;</span>
    if (!c.in_scope) {
      // Young items are healthy, not broken: they list at their launch price
      // and only enter repricing scope at min_age_months. Show the price +
      // a "launch" tag instead of the cryptic "under_age". current_cents is
      // the launch price already returned by the API — no JS pricing math.
      if (c.skip_reason === 'under_age') {
        return (
          <div>
            <div>{formatPence(c.current_cents)}</div>
            <span
              style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 500 }}
              title="At launch price — the engine takes over from the configured minimum age"
            >
              launch
            </span>
          </div>
        )
      }
      return (
        <div>
          <div>{formatPence(c.current_cents)}</div>
          <div style={{ color: '#6b7280', fontSize: 10 }} title="Not in scope for repricing">
            {SKIP_REASON_LABELS[c.skip_reason] || c.skip_reason || 'skipped'}
          </div>
        </div>
      )
    }
    const raise = c.proposed_cents != null && c.current_cents != null && c.proposed_cents > c.current_cents
    // Loss signal is NET of the channel fee (below_cost_after_fee), NOT the gross
    // below_cost — the gross flag misses fee-driven losses (biggest on eBay).
    const belowCost = !!c.below_cost_after_fee
    const color = belowCost ? '#ef4444' : raise ? '#f59e0b' : '#22c55e'
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ color: '#6b7280', fontSize: 11 }}>{formatPence(c.current_cents)}</span>
        <span style={{ fontWeight: 600, color }} title={
          (raise ? 'Would raise' : 'Would lower') + (belowCost ? ' — loses money after fee' : '')
        }>
          → {formatPence(c.proposed_cents)}
        </span>
        {c.margin_at_proposed_cents != null && (
          <span
            style={{ fontSize: 10, color: belowCost ? '#ef4444' : '#6b7280' }}
            title="Net margin after the channel fee at the proposed price"
          >
            {formatPence(c.margin_at_proposed_cents)} margin
          </span>
        )}
      </div>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Pricing Management</h2>
        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
          Per-product anchor and the engine's price per channel.
        </p>
      </div>

      {/* Engine-mode banner: LIVE / dry-run / halted, from pricing_config */}
      {(() => {
        const rows = {}
        for (const r of Array.isArray(config) ? config : []) rows[r.channel] = r
        const master = rows['*']
        const d2c = rows.shopify
        const known = master != null && d2c != null
        const halted = known && (!master.enabled || !d2c.enabled)
        const live = known && !halted && !d2c.dry_run
        const asOf = summary && (
          <span style={{ color: '#6b7280' }}>
            {' '}Summary as of {summary.computed_at ? new Date(summary.computed_at).toLocaleString() : 'now (live)'}.
          </span>
        )
        if (live) {
          return (
            <div className="card" style={{ padding: '10px 16px', marginBottom: 16, background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.35)' }}>
              <div style={{ fontSize: 12, color: '#16a34a' }}>
                <strong>● Engine LIVE on D2C.</strong> A nightly job (02:35 UTC, capped per run) pushes curve
                prices to Shopify — down-only, never below cost + floor %. eBay &amp; Reverb follow via the
                channel fan-out. The <strong>anchor</strong> is auto-set at activation and read-only. Each
                channel cell shows <em>current → curve</em>.{asOf}
              </div>
            </div>
          )
        }
        if (halted) {
          return (
            <div className="card" style={{ padding: '10px 16px', marginBottom: 16, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.35)' }}>
              <div style={{ fontSize: 12, color: '#ef4444' }}>
                <strong>■ Engine HALTED.</strong> {!master?.enabled ? 'The master kill switch is OFF' : 'The D2C channel is disabled'} —
                nothing is pushed anywhere (see Rules to re-enable). Prices shown are what the curve would do.{asOf}
              </div>
            </div>
          )
        }
        return (
          <div className="card" style={{ padding: '10px 16px', marginBottom: 16, background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            <div style={{ fontSize: 12, color: '#3b82f6' }}>
              <strong>Dry-run preview.</strong> The engine computes the price each item <em>would</em> list at;
              nothing is pushed to any marketplace. The <strong>anchor</strong> is auto-set at activation and is
              read-only. Each channel cell shows <em>current → proposed</em>.{asOf}
            </div>
          </div>
        )
      })()}

      {/* KPI row (whole-universe, from the persisted summary) */}
      {kpis && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'In stock', value: total, color: '#3b82f6', title: 'Active, in-stock products in the pricing universe.' },
            { label: 'Total Value', value: formatPence(totals.value), color: '#3b82f6', title: 'Sum of current D2C prices over the in-stock products shown (follows the filters). Each price is multiplied by its stock quantity.' },
            { label: 'Total Cost', value: formatPence(totals.cost), color: '#3b82f6', title: 'Sum of buy costs over the in-stock products shown (follows the filters). Each cost is multiplied by its stock quantity; a product with no cost recorded adds £0.' },
            { label: 'Σ exposure (D2C)', value: formatPence(kpis.exposure), color: kpis.exposure >= 0 ? '#f59e0b' : '#22c55e', title: 'Sum of (curve − current) over D2C. eBay/Reverb follow D2C via the fan-out and are not counted.' },
            { label: 'Held by clamp', value: kpis.wouldRaise, color: '#f59e0b', title: 'D2C items whose curve price sits ABOVE the current price (mostly earlier manual cuts). The down-only clamp holds them — the engine never raises a price.' },
            { label: 'Below cost (D2C)', value: kpis.belowCost, color: '#ef4444', title: 'D2C items whose curve price is below buy cost. The floor clamp stops pushes below cost + floor %.' },
          ].map(kpi => (
            <div key={kpi.label} className="card" style={{ padding: '10px 16px', minWidth: 110 }} title={kpi.title}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>{kpi.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters — house design system (.card → .filter-section → .filter-group) */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-section">
          <div className="filter-group filter-group-wide">
            <label className="filter-label">Product Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by product name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters() }}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Category</label>
            <select className="form-input" value={mainCat} onChange={e => setMainCat(e.target.value)}>
              <option value="all">All categories</option>
              {MAIN_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Age</label>
            <MultiSelectDropdown
              options={AGE_MONTH_OPTIONS}
              selected={ageMonths}
              onChange={setAgeMonths}
              placeholder="All ages"
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Price Band</label>
            <select className="form-input" value={band} onChange={e => setBand(e.target.value)}>
              <option value="all">All price bands</option>
              {PRICE_BANDS.map(b => <option key={b.label} value={b.label}>{b.label}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Market Price</label>
            <select
              className="form-input"
              aria-label="Market price filter"
              value={marketPrice}
              onChange={e => setMarketPrice(e.target.value)}
              title="Filter to products that have (or don't have) a looked-up market price"
            >
              <option value="all">All</option>
              <option value="has">Has market price</option>
              <option value="none">No market price</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Stock type</label>
            <select
              className="form-input"
              aria-label="Ownership filter"
              value={ownership}
              onChange={e => setOwnership(e.target.value)}
              title="Filter to owned stock, consignment stock, or both"
            >
              <option value="all">All</option>
              <option value="owned">Owned</option>
              <option value="consignment">Consignment</option>
            </select>
          </div>
        </div>

        <button type="button" className="filter-toggle" onClick={() => setShowAdvanced(s => !s)}>
          <span className={`filter-toggle-icon ${showAdvanced ? 'expanded' : ''}`}>▼</span>
          {showAdvanced ? 'Hide' : 'Show'} legacy filters
          {(productIds || createdFrom || createdTo) && <span className="filter-toggle-active">(active)</span>}
        </button>

        {showAdvanced && (
          <div className="advanced-filters">
            <div className="filter-section">
              <div className="filter-group filter-group-wide">
                <label className="filter-label">Product IDs</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Comma-separated"
                  value={productIds}
                  onChange={e => setProductIds(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') applyFilters() }}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Created From</label>
                <input type="date" className="form-input" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} />
              </div>
              <div className="filter-group">
                <label className="filter-label">Created To</label>
                <input type="date" className="form-input" value={createdTo} onChange={e => setCreatedTo(e.target.value)} />
              </div>
              <div className="filter-group">
                <label className="filter-label">Older Than</label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  {[{ m: 6, label: '6 mo' }, { m: 12, label: '1 yr' }, { m: 24, label: '2 yr' }].map(q => (
                    <button key={q.m} type="button" className="btn btn-sm btn-secondary" onClick={() => { setCreatedTo(isoMonthsAgo(q.m)); setCreatedFrom('') }}>
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {(productIds || createdFrom || createdTo) && (
              <div className="filter-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setProductIds(''); setCreatedFrom(''); setCreatedTo('') }}>
                  Clear legacy filters
                </button>
              </div>
            )}
          </div>
        )}

        <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
          <button
            type="button"
            className={`btn btn-sm ${hasPendingChanges ? 'btn-primary' : 'btn-secondary'}`}
            onClick={applyFilters}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Apply filters'}
          </button>
          <button type="button" className="btn btn-sm btn-secondary" onClick={clearFilters} disabled={loading}>
            Clear
          </button>
          {hasPendingChanges && (
            <span className="text-sm text-muted" style={{ alignSelf: 'center' }}>Unapplied changes</span>
          )}
        </div>
      </div>

      {/* Products table */}
      <div className="card" style={{ padding: 0 }}>

        {/* Bulk market-price lookup progress (shown whenever a job is tracked, even
            after the selection is cleared or the page is reloaded mid-run). */}
        {bulk.job && (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', background: 'rgba(59, 130, 246, 0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Market-price lookup</strong>
              <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                <ProgressBar
                  current={(bulk.job.done || 0) + (bulk.job.skipped || 0) + (bulk.job.errors || 0)}
                  total={bulk.job.total || 0}
                  status={bulkStatusText(bulk.job)}
                />
              </div>
              <button className="btn btn-sm btn-secondary" onClick={bulk.dismiss}>
                {bulk.running ? 'Hide' : 'Dismiss'}
              </button>
            </div>
            {bulk.running && (
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Runs in the background — you can leave this page; progress resumes when you return.
              </div>
            )}
          </div>
        )}
        {bulk.error && (
          <div style={{ padding: '8px 16px', color: '#ef4444', fontSize: 12, borderBottom: '1px solid var(--border-color)' }}>{bulk.error}</div>
        )}

        {/* Bulk actions (shown when rows are selected) */}
        {selected.size > 0 && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: 10, alignItems: 'center', background: 'rgba(150, 191, 72, 0.10)', fontSize: 12 }}>
            <span style={{ fontWeight: 600 }}>{selected.size} selected</span>
            {canWrite && (
              <button
                className="btn btn-sm btn-secondary"
                disabled={bulk.running}
                title="Look up the market price for the selected items (skips any already priced this month)"
                onClick={startBulkLookup}
              >
                Lookup Market price ({selected.size})
              </button>
            )}
            {canWrite && (
              <button
                className="btn btn-sm btn-primary"
                title="Step through the selected items one at a time to reprice quickly"
                onClick={startReview}
              >
                Review &amp; reprice ({selected.size})
              </button>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleSelectAll} />
                </th>
                {sortTh('product', undefined)}
                {sortTh('category', 200)}
                {sortTh('age', 60)}
                {sortTh('anchor', 120)}
                {sortTh('cost', 90)}
                <th style={{ width: 90 }} title="D2C cost floor at the configured minimum margin — display-only, not enforced">Floor</th>
                <th style={{ width: 110 }} title="Market sell-price (reference) and the demand read of the latest lookup — open the listing for detail">Mkt</th>
                {sortTh('last_repriced', 110)}
                <th style={{ width: 110 }}><span style={{ color: CHANNEL_COLORS.shopify.dot }}>● </span>D2C</th>
                <th style={{ width: 110 }}><span style={{ color: CHANNEL_COLORS.ebay.dot }}>● </span>eBay</th>
                <th style={{ width: 110 }}><span style={{ color: CHANNEL_COLORS.reverb.dot }}>● </span>Reverb</th>
                <th style={{ width: 80 }}>Fixed</th>
                <th style={{ width: 48 }}>SALE</th>
                {canWrite && <th style={{ width: 70 }}>Edit</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.shopify_product_id}>
                  <td>
                    <input type="checkbox" checked={selected.has(p.shopify_product_id)} onChange={() => toggleSelect(p.shopify_product_id)} />
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        className="link-button"
                        style={{ fontWeight: 500, textAlign: 'left' }}
                        title="Open the listing: photos and description"
                        onClick={() => openDetail(p.shopify_product_id)}
                      >
                        {p.title || p.shopify_product_id}
                      </button>
                      {p.is_consignment && (
                        <ConsignmentBadge
                          title={p.consignment_floor_pct != null
                            ? `Consignment — descends to a ${p.consignment_floor_pct}% floor on its own curve`
                            : 'Consignment — on its markdown curve'}
                        />
                      )}
                    </div>
                    <div className="text-sm text-muted" style={{ fontFamily: 'monospace', fontSize: 10 }}>{p.shopify_product_id}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{p.main_category}</div>
                    <div style={{ color: '#6b7280', fontSize: 11 }}>{p.sub_category}</div>
                  </td>
                  <td>
                    <span style={{ color: p.age_months >= 9 ? '#ef4444' : p.age_months >= 6 ? '#f59e0b' : '#6b7280', fontWeight: 600 }}>
                      {p.age_months} mo
                    </span>
                  </td>
                  <td>
                    {p.is_consignment ? (
                      // Consignment prices off the frozen agreed price, NOT the owned
                      // pricing anchor — showing the anchor here misleads (it's just a
                      // leftover first-live-price capture that the curve never reads).
                      <span style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        title="Agreed consignment listing price — the curve descends from here to the promised floor. The owned pricing anchor does not apply.">
                        {p.consignment_start_cents != null ? formatPence(p.consignment_start_cents) : '—'}
                        <span style={{ color: CONSIGNMENT_COLOR, fontSize: 10 }}>agreed</span>
                      </span>
                    ) : (
                      <span style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Anchor — auto-set at activation, immutable.">
                        {formatPence(p.anchor_cents)}
                        <span style={{ color: '#6b7280', fontSize: 10 }}>🔒</span>
                        <span style={{ color: '#9ca3af', fontSize: 10 }}>{anchorBandLabel(p.anchor_cents)}</span>
                      </span>
                    )}
                  </td>
                  <td style={{ color: '#6b7280' }}>{formatPence(p.cost_cents)}</td>
                  <td style={{ color: '#6b7280' }}
                    title={p.is_consignment
                      ? `Promised floor (agreed × ${p.consignment_floor_pct != null ? p.consignment_floor_pct + '%' : 'the frozen %'}) — enforced; the engine never pushes below it.`
                      : 'D2C cost floor at the configured minimum margin (display-only)'}>
                    {formatPence(p.floor_cents)}
                  </td>
                  <td>
                    <div title="Market sell-price (reference)">{formatPence(p.sp_cents)}</div>
                    {p.demand_tier && (
                      <span className={`badge badge-sm ${DEMAND_TONE[p.demand_tier] || 'badge-neutral'}`} title={DEMAND_TIP} style={{ whiteSpace: 'nowrap' }}>
                        {p.demand_tier}{p.sold_count != null ? ` · ${p.sold_count} sold` : ''}
                      </span>
                    )}
                  </td>
                  <td style={{ color: '#6b7280', fontSize: 11, whiteSpace: 'nowrap' }} title="Last time the selling price actually changed (reprice or observed change)">
                    {formatLastRepriced(p.last_repriced_at)}
                  </td>
                  <td>{renderChannelCell(p, 'shopify')}</td>
                  <td>{renderChannelCell(p, 'ebay')}</td>
                  <td>
                    {renderChannelCell(p, 'reverb')}
                    {p.reverb_watches > 0 && (
                      <div>
                        <span
                          className="badge badge-sm badge-info"
                          style={{ whiteSpace: 'nowrap' }}
                          title={`${p.reverb_watches} ${p.reverb_watches === 1 ? 'person is' : 'people are'} watching this listing on Reverb (count from the nightly listing walk)`}
                        >
                          W: {p.reverb_watches}
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    {p.has_override
                      ? <span style={{ background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 }}>Fixed</span>
                      : <span style={{ color: '#6b7280', fontSize: 11 }}>&mdash;</span>}
                  </td>
                  <td>
                    {/* Read-only: SALE membership strictly follows the compare-at
                        price (set via Edit / Review & reprice), enforced backend-side. */}
                    {p.in_sale ? (
                      <span
                        style={{ background: '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}
                        title="In the SALE collection — items with a compare-at price are on SALE automatically"
                      >
                        Y
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280' }} title="No compare-at price — not in the SALE collection">N</span>
                    )}
                  </td>
                  {canWrite && (
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => setEditProduct(p)}
                        title="Edit the D2C price and compare-at"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>}
        {error && <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
            <p>No active, in-stock products match these filters.</p>
          </div>
        )}

        {/* Pagination footer (server-side; mirrors InventoryList) */}
        {total > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">Showing {pageStart}–{pageEnd} of {total} active, in-stock</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <button type="button" className="btn btn-sm btn-secondary" disabled={offset === 0 || loading} onClick={() => loadPage(Math.max(0, offset - pageSize))}>Back</button>
                <span className="text-sm" style={{ minWidth: 90, textAlign: 'center' }}>Page {currentPage} of {totalPages}</span>
                <button type="button" className="btn btn-sm btn-secondary" disabled={pageEnd >= total || loading} onClick={() => loadPage(offset + pageSize)}>Next</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', borderLeft: '1px solid var(--color-border)', paddingLeft: 'var(--space-3)' }}>
                <span className="text-sm text-muted">Per page:</span>
                {PAGE_SIZE_OPTIONS.map(n => (
                  <button
                    key={n}
                    type="button"
                    className={`btn btn-sm ${pageSize === n ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setPageSize(n)}
                    disabled={loading}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
          <strong>How to read this page.</strong> The engine is <strong>live on D2C</strong>: a nightly job
          (02:35 UTC, capped per run) pushes curve prices to Shopify — <em>down-only</em> (it never raises a
          price) and never below <em>buy cost + the floor %</em> (see Rules). eBay &amp; Reverb are not pushed
          directly — they follow the D2C price via the channel fan-out, so their cells are informational.
          Each cell shows the <em>current</em> listed price and the curve price (green = engine will lower it
          on an upcoming run, amber = curve sits above current so the clamp holds it, red = below cost).
          Out-of-scope cells show their skip reason. Engine pushes appear in the Audit Log as
          <strong> ⚙ Curve engine</strong>.
          <br />
          <strong>Anchor:</strong> the reference price the curve multiplies — set when the item is first priced
          (older stock is backfilled from its price history) and locked (🔒). The small label is its price band.
          Items reprice on their monthly age anniversary, month 1 = the listing month, floor at M12.
          <br />
          <strong>Fixed:</strong> a per-channel override pins the price off the curve — the nightly job skips
          pinned items entirely. Use <strong>Edit</strong> to set the D2C price/compare-at (pins the shopify
          override); the modal's <strong>Return to curve</strong> clears it.
          <br />
          <strong>SALE:</strong> membership follows the compare-at price automatically — set a compare-at (via
          Edit or Review &amp; reprice) and the item joins the Shopify SALE collection; clear it and the item
          leaves. The engine never touches compare-at. <strong>Research</strong> links (Reverb / eBay / Google
          searches) live in the Edit modal.
        </p>
      </div>

      {editProduct && (
        <EditPriceModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSaved={() => loadPage(offset)}
        />
      )}

      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          showMarketPrice
          onClose={() => setDetailProduct(null)}
        />
      )}

      {reviewItems && (
        <RepriceReviewOverlay
          items={reviewItems}
          onClose={() => { setReviewItems(null); setSelected(new Set()); loadPage(offset) }}
        />
      )}
    </div>
  )
}

export default PricingManagement
